import omit from "lodash/omit";
import { GetStaticProps, NextPage } from "next";
import Image from "next/image";
import { MDXRemote } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import React, { useContext } from "react";
import remarkGfm from "remark-gfm";

import { CodeBlock, CodeContainer } from "@baseComponents/CodeBlock";
import { BlogPostLayout } from "@components/BlogPostLayout";
import { ContentCard } from "@components/ContentCard";
import { HoudiniDemo } from "@components/HoudiniDemo";
import { ServiceReferralCard } from "@components/ServiceReferralCard";
import { SVGDemo } from "@components/SVGDemo";
import { AppContext, ThemeMode } from "@context/context";
import { baseStaticProps } from "@lib/baseStaticProps";
import { BlogPostPageProps, getPost, getPosts } from "@lib/blog";

export const getStaticProps: GetStaticProps = async context => {
  const post = await getPost(context.params!.post as string);
  const locale = context.locale;
  const localizedContent = post.localizedContent[locale!];

  return {
    props: {
      ...(await baseStaticProps({ locale })),
      post: omit(localizedContent, "content"),
      source: await serialize(localizedContent.content, {
        mdxOptions: { remarkPlugins: [remarkGfm] },
      }),
    },
  };
};

export async function getStaticPaths() {
  const posts = await getPosts();

  return {
    paths: posts.flatMap(post =>
      Object.keys(post.localizedContent).map(l => ({
        params: { post: post.shortName },
        locale: l,
      })),
    ),
    fallback: false,
  };
}

const ThemedPage = (props: BlogPostPageProps) => {
  const appContext = useContext(AppContext);

  return (
    <>
      <BlogPostLayout {...props}>
        <MDXRemote
          {...props.source}
          components={{
            img: ({ src, alt }) => {
              if (!src) return null;

              const finalSrc = src.includes("/-theme-/")
                ? appContext.theme === ThemeMode.Light
                  ? src.replace("/-theme-/", "/light/")
                  : src.replace("/-theme-/", "/dark/")
                : src;

              return (
                <Image
                  src={finalSrc}
                  alt={alt ?? ""}
                  width={800}
                  height={600}
                />
              );
            },
            ContentCard: ({ children }) => (
              <ContentCard>{children}</ContentCard>
            ),
            ServiceReferralCard: props => <ServiceReferralCard {...props} />,
            code: ({ children, className }) => (
              <CodeBlock code={children as string} className={className} />
            ),
            pre: ({ children }) => <CodeContainer>{children}</CodeContainer>,
            a: ({ children, href }) => (
              <a href={href} target="_blank" rel="noopener">
                {children}
              </a>
            ),
            HoudiniDemo: () => <HoudiniDemo />,
            SVGDemo: () => <SVGDemo />,
          }}
        />
      </BlogPostLayout>
    </>
  );
};

const BlogPost: NextPage<BlogPostPageProps> = props => {
  return <ThemedPage {...props} />;
};

export default BlogPost;
